import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Alert,
  AlertIcon,
  useColorModeValue,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { usePartnerTasks } from "hooks/usePartnerTasks";
import { getStatusLabel } from "utils/taskStatus";
import { formatCurrency } from "utils/formatCurrency";
import { formatDate } from "utils/formatDate";
import type { TaskStatus } from "types/fulfillment";
import { useNavigate } from "react-router-dom";
import Card from "components/card/Card";
import { OrderStatusBadge } from "views/partner/components/OrderStatusBadge";

const ALL_STATUSES: TaskStatus[] = [
  "new",
  "preparing",
  "shipped",
  "delivered",
  "cancelled",
];

export default function TasksList() {
  const { tasks, loading, error, refetch } = usePartnerTasks();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<TaskStatus | "all">("all");
  const [search, setSearch] = useState("");

  const headingColor = useColorModeValue("secondaryGray.900", "white");
  const mutedColor = useColorModeValue("secondaryGray.600", "secondaryGray.600");
  const rowHoverBg = useColorModeValue("gray.50", "navy.700");
  const inputBg = useColorModeValue("white", "navy.800");

  const filtered = useMemo(() => {
    let result = tasks;
    if (statusFilter !== "all") {
      result = result.filter((t) => t.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (t) =>
          t.orderId?.toLowerCase().includes(q) ||
          t.customerName?.toLowerCase().includes(q) ||
          t.deliveryCity?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [tasks, statusFilter, search]);

  return (
    <Box>
      <Flex mb={6} align={{ base: "start", md: "center" }} justify="space-between" direction={{ base: "column", md: "row" }} gap={4}>
        <Box>
          <Heading size="lg" color='transparent' mb={1}>.</Heading>
          <Text fontSize="sm" color='transparent'>.</Text>
        </Box>
        <Button size="sm" variant="outline" onClick={refetch}>
          Atualizar
        </Button>
      </Flex>

      <Flex mb={4} direction={{ base: "column", md: "row" }} gap={3}>
        <InputGroup maxW={{ md: "320px" }} bg={inputBg} borderRadius="30px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            borderRadius="30px"
            placeholder="Pedido, cliente ou cidade…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            border="none"
            _focus={{ boxShadow: "none" }}
          />
        </InputGroup>
        <ButtonGroup size="sm" variant="outline" flexWrap="wrap" spacing={2}>
          <Button
            colorScheme={statusFilter === "all" ? "brand" : "gray"}
            variant={statusFilter === "all" ? "solid" : "outline"}
            onClick={() => setStatusFilter("all")}
          >
            Todos
          </Button>
          {ALL_STATUSES.map((s) => (
            <Button
              key={s}
              colorScheme={statusFilter === s ? "brand" : "gray"}
              variant={statusFilter === s ? "solid" : "outline"}
              onClick={() => setStatusFilter(s)}
            >
              {getStatusLabel(s)}
            </Button>
          ))}
        </ButtonGroup>
      </Flex>

      {loading && (
        <Flex justify="center" py={12}>
          <Spinner size="lg" color="brand.500" thickness="4px" />
        </Flex>
      )}

      {error && (
        <Alert status="error" borderRadius="xl" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {!loading && !error && filtered.length === 0 && (
        <Card py={8}>
          <Text textAlign="center" color={mutedColor}>
            Nenhum pedido encontrado.
          </Text>
        </Card>
      )}

      {!loading && !error && filtered.length > 0 && (
        <>
          <Card display={{ base: "none", md: "block" }} p={0} overflow="hidden">
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th color={mutedColor}>Pedido</Th>
                    <Th color={mutedColor}>Data</Th>
                    <Th color={mutedColor}>Itens</Th>
                    <Th color={mutedColor}>Subtotal</Th>
                    <Th color={mutedColor}>Cidade/UF</Th>
                    <Th color={mutedColor}>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filtered.map((task) => (
                    <Tr
                      key={task.id}
                      cursor="pointer"
                      _hover={{ bg: rowHoverBg }}
                      onClick={() => navigate(`/partner/orders/${task.id}`)}
                    >
                      <Td fontWeight="medium" color={headingColor}>
                        {task.orderId?.slice(0, 8) ?? "—"}
                      </Td>
                      <Td color={mutedColor}>{formatDate(task.createdAt)}</Td>
                      <Td color={mutedColor}>{task.itemCount}</Td>
                      <Td color={mutedColor}>{formatCurrency(task.subtotal)}</Td>
                      <Td color={mutedColor}>
                        {task.deliveryCity && task.deliveryState
                          ? `${task.deliveryCity}/${task.deliveryState}`
                          : "—"}
                      </Td>
                      <Td>
                        <OrderStatusBadge status={task.status} />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Card>

          <Flex direction="column" gap={3} display={{ base: "flex", md: "none" }}>
            {filtered.map((task) => (
              <Card
                key={task.id}
                py={4}
                cursor="pointer"
                onClick={() => navigate(`/partner/orders/${task.id}`)}
                _hover={{ bg: rowHoverBg }}
              >
                <Flex mb={2} justify="space-between" align="center">
                  <Text fontWeight="medium" color={headingColor}>
                    #{task.orderId?.slice(0, 8)}
                  </Text>
                  <OrderStatusBadge status={task.status} />
                </Flex>
                <Flex justify="space-between" fontSize="sm" color={mutedColor}>
                  <Text>{formatDate(task.createdAt)}</Text>
                  <Text>
                    {task.itemCount} {task.itemCount === 1 ? "item" : "itens"}
                  </Text>
                  <Text fontWeight="medium" color={headingColor}>
                    {formatCurrency(task.subtotal)}
                  </Text>
                </Flex>
              </Card>
            ))}
          </Flex>
        </>
      )}
    </Box>
  );
}
